import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import AuthLayout from './AuthLayout';

export default function LoginScreen({ onSwitchToSignup, onSwitchToReset }) {
  const { signIn, verifyMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  // Audit 2026-07-01: two-step flow. After password succeeds, if the
  // account has a verified TOTP factor, signIn returns
  // { mfaRequired: true, factorId }. We then render the code prompt
  // and call verifyMfa to upgrade the session from aal1 → aal2.
  const [mfa, setMfa] = useState(null);   // { factorId } or null
  const [code, setCode] = useState('');

  const submit = async () => {
    if (pending) return;
    setError('');
    setPending(true);
    const result = await signIn(email.trim(), password);
    setPending(false);
    if (result.mfaRequired) {
      setMfa({ factorId: result.factorId });
      setCode('');
      return;
    }
    if (!result.ok) setError(result.error || 'Could not sign in.');
  };

  const verifyCode = async () => {
    if (pending) return;
    setError('');
    setPending(true);
    const r = await verifyMfa({ factorId: mfa.factorId, code });
    setPending(false);
    if (!r.ok) {
      setError(r.error || 'Code did not verify. Try again.');
      return;
    }
    // Auth state changes cascade through the AuthContext listener —
    // the app will re-render into the signed-in tree automatically.
  };

  const cancelMfa = () => {
    setMfa(null);
    setCode('');
    setError('');
  };

  const onKey = (e) => { if (e.key === 'Enter') (mfa ? verifyCode() : submit()); };
  const canSubmit = !!email.trim() && !!password && !pending;
  const canVerify = code.replace(/\D/g, '').length === 6 && !pending;

  if (mfa) {
    return (
      <AuthLayout
        eyebrow="Two-factor sign-in"
        heading="Enter your code"
        subheading="Open your authenticator app and type the current 6-digit code for this account."
        footer={
          <button
            onClick={cancelMfa}
            className="font-semibold text-slate-500 hover:text-slate-800 hover:underline"
          >
            ← Use a different account
          </button>
        }
      >
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">6-digit code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={code}
              onChange={e => { setCode(e.target.value); setError(''); }}
              onKeyDown={onKey}
              placeholder="123456"
              maxLength={6}
              autoFocus
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-lg font-mono tracking-widest text-center focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
            />
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}
          <button
            onClick={verifyCode}
            disabled={!canVerify}
            className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
          >
            {pending ? 'Verifying…' : 'Verify + sign in'}
          </button>
          <div className="text-[11px] text-slate-500 text-center leading-relaxed">
            Lost your device? Reply to any Six Figure Agent OS email and we'll help you unlock the account after we verify identity.
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Welcome back"
      heading="Sign in"
      subheading="Pick up right where you left off."
      footer={
        <>
          Don't have an account yet?{' '}
          <button
            onClick={onSwitchToSignup}
            className="font-semibold text-amber-700 hover:text-amber-800 hover:underline"
          >
            Create one →
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="you@example.com"
            autoFocus
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        <label className="block">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Password</span>
            <button
              type="button"
              onClick={onSwitchToReset}
              className="text-xs font-semibold text-amber-700 hover:text-amber-800 hover:underline"
            >
              Forgot?
            </button>
          </div>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="••••••••"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
            <span aria-hidden="true">⚠</span>
            <span>{error}</span>
          </div>
        )}
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
        >
          {pending ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
    </AuthLayout>
  );
}
